<template>
  
  <component :is="defineLayouts()">
    <router-view />
    <Toast />
    <Dialog />
    <LogEvent :show="appStore.state.showLogEvent" />
    <SubscriptionModal />
    <SyncStatusModal />
    <TriadTunerBar />
    <TriadTunerModal />
    <TriadMetronome />
  </component>
  
</template>

<script setup>
import { useRoute } from 'vue-router'
import { onMounted, ref } from 'vue'
import { realtimeService } from '@/service/realtime.service'
import toastStore from '@/store/toast.store';
import userStore from '@/store/user.store';
import appStore from '@/store/app.store';
import { appEvents } from '@/store/app.events';
import LogEvent from '@/components/common/LogEvent.vue';
import SubscriptionModal from '@/components/subscription/SubscriptionModal.vue';
import SyncStatusModal from '@/components/common/elements/SyncStatusModal.vue';
import TriadTunerBar from '@/components/audio/TriadTunerBar.vue';
import TriadTunerModal from '@/components/audio/TriadTunerModal.vue';
import TriadMetronome from '@/components/audio/TriadMetronome.vue';
import { onBeforeUnmount } from 'vue';
import { pushService } from '@/service/push.service';
import syncService from '@/service/sync.service';
import { isNativePlatform, openAuthSession } from '@/utils/capacitor';
import { checkForAppUpdate, initAppUpdateListeners } from '@/service/appUpdate.service';
import { initRevenueCat } from '@/service/subscription.service';
import { useRouter } from 'vue-router';

const CHANNELS_STORAGE_KEY = 'notification_channels'
const connected = ref(false)
const retryTimers = {}
const router = useRouter();

const defineLayouts = () => {
  const r = useRoute()
  if (r.meta && r.meta.layout) {
    return r.meta.layout;
  }
  const publicRoutes = ['Login', 'Terms', 'PrivacyPolicy', 'CopyrightPolicy', 'Error', 'UpdateRequired'];
  if (!publicRoutes.includes(r.name) && !(r.meta && r.meta.requiresAuth === false)) {
    const u = userStore.state.userInfo;
    if (!u || (!u.id && !u.email)) {
      return 'EmptyLayout';
    }
  }
  return 'AppLayoutV2';
}

const saveChannel = (channelName, eventName = '*') => {
  try {
    const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]')
    const idx = saved.findIndex(s => s.channel === channelName)
    const entry = { channel: channelName, event: eventName }
    if (idx === -1) {
      saved.push(entry)
    } else {
      saved[idx] = entry
    }
    localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(saved))
  } catch (e) {
    console.error('Error saving channel to localStorage:', e)
  }
}

const restoreChannels = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]')
    saved.forEach(({ channel: channelName, event: eventName }) => {
      subscribeToChannel(channelName, eventName)
    })
  } catch (e) {
    console.error('Error restoring channels from localStorage:', e)
  }
}

const subscribeToChannel = (channelName, eventName = '*') => {
  if (retryTimers[channelName]) {
    return
  }
  realtimeService.subscribe(
    channelName, 
    eventName, 
    (payload) => onEvent({event: payload.event, data: payload}),
    (status, err) => {
      if (status == 'SUBSCRIBED') {
        connected.value = true
        saveChannel(channelName, eventName)
      } else if (status == 'CHANNEL_ERROR' || status == 'TIMED_OUT') {
        console.error('Channel error:', channelName, status, err)
        retryTimers[channelName] = setTimeout(() => {
          delete retryTimers[channelName]
          subscribeToChannel(channelName, eventName)
        }, 10000)
      } else {
        connected.value = false
      }
    }
  )
}

onMounted(() => {
  // Initialize theme
  if (localStorage.getItem('color-theme') === 'dark' || (!('color-theme' in localStorage) && localStorage.getItem('theme') === 'dark')) {
    document.documentElement.classList.add('dark');
    localStorage.setItem('color-theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('color-theme', 'light');
  }

  const params = new URLSearchParams(window.location.search)
  const autoLogin = params.get('auto_login')
  if (autoLogin === 'true') {
    if (isNativePlatform()) {
      openAuthSession('/login?force=true');
    } else {
      window.location.href = '/login?force=true';
    }
    return
  }

  const playlistId = params.get('playlist')
  const songId = params.get('song')
  if (playlistId && songId) {
    window.location.href = window.location.origin+"/#/playlist/"+playlistId+"/song/"+songId
  } else {
    if (playlistId) {
      window.location.href = window.location.origin+"/#/playlist/"+playlistId
    }
  }

  // Listen for Service Worker updates and Network Connectivity
  window.addEventListener('sw-updated', handleSWUpdated);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Sessão expirada/inválida (emitido pelo interceptor fetch em 401 nativo):
  // limpa o store em memória e volta ao login em vez de deixar tela em branco.
  appEvents.on('app:session-expired', handleSessionExpired);

  // Restaura a última rota autenticada se o processo morreu (ex.: file picker
  // do sistema matou a activity e o boot caiu no '/').
  try {
    const saved = localStorage.getItem('triad_last_route');
    const u = userStore.state.userInfo;
    if (saved && saved !== '/' && !saved.startsWith('/login') &&
        u && (u.id || u.email) && router.currentRoute.value.path === '/') {
      router.replace(saved);
    }
  } catch (e) {
    console.warn('Route restore failed:', e);
  }

  // Push nativo (FCM): mostra toast quando chegar com o app aberto.
  // Web (FCM): idem via foreground listener.
  try {
    pushService.ensureNativeListeners((notification) => {
      toastStore.dispatch("addMessage", {
        message: notification.body || notification.title || 'Nova notificação',
        icon: 'info',
        ttl: 5000
      });
    });
    if (!isNativePlatform()) {
      import('@/service/firebase.service').then(({ onForegroundMessage }) => {
        onForegroundMessage((payload) => {
          const n = (payload && payload.notification) || {};
          toastStore.dispatch("addMessage", {
            message: n.body || n.title || 'Nova notificação',
            icon: 'info',
            ttl: 5000
          });
        });
      }).catch(() => {});
    }
  } catch (e) {
    // push indisponível — segue sem listeners
  }

  // If online at start, check for any queued items from offline session
  if (navigator.onLine) {
    syncService.processQueue();
  }

  // Check for mobile app updates & register foreground resume listener
  checkForAppUpdate().catch((e) => console.warn('App update check error:', e));
  initAppUpdateListeners();
})

const handleSessionExpired = () => {
  userStore.commit('CLEAR_USER');
  import('@/utils/authCleanup').then(({ clearAllUserCaches }) => clearAllUserCaches()).catch(() => {});
  Object.keys(retryTimers).forEach((channel) => {
    clearTimeout(retryTimers[channel]);
    delete retryTimers[channel];
  });
  connected.value = false;
  toastStore.dispatch("addMessage", {
    message: 'Sua sessão expirou. Entre novamente.',
    icon: 'warning',
    ttl: 4000
  });
  try {
    if (router.currentRoute.value.name !== 'Login') {
      if (isNativePlatform()) {
        router.replace('/login');
      } else {
        window.location.href = '/login';
      }
    }
  } catch (e) {
    console.warn('Session-expired redirect failed:', e);
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('sw-updated', handleSWUpdated);
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  appEvents.off('app:session-expired', handleSessionExpired);
})

const handleOnline = () => {
  appStore.commit('SET_ONLINE_STATUS', true);
  toastStore.dispatch("addMessage", {
    message: 'Conexão com a internet restabelecida.',
    icon: 'success',
    ttl: 3000
  });
  if (userStore.state.userInfo) {
    restoreChannels();
  }
  // Aguarda o replay da fila ANTES de recarregar: senão o reload busca o backend
  // ainda sem as edições e o cliente fica com a versão antiga (só outros veem a nova).
  Promise.race([
    Promise.resolve(syncService.drain()),
    new Promise((resolve) => setTimeout(resolve, 20000))
  ]).catch(() => {}).finally(() => {
    appEvents.emit('app:online');
  });
};

const handleOffline = () => {
  appStore.commit('SET_ONLINE_STATUS', false);
  toastStore.dispatch("addMessage", {
    message: 'Você está no modo offline. As músicas e playlists salvas estão disponíveis para leitura.',
    icon: 'warning',
    ttl: 5000
  });
};

const handleSWUpdated = (event) => {
  const registration = event.detail;
  toastStore.dispatch("addMessage", {
    message: `A new version of the app is available! <a href="#" style="text-decoration: underline; font-weight: bold;" id="reload-app-btn">Click here to reload</a>.`, 
    icon: 'info', 
    ttl: 30000
  });

  // Since Toast doesn't support complex callbacks directly, we can add a delegated listener
  setTimeout(() => {
    const btn = document.getElementById('reload-app-btn');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  }, 100);
}

const onEvent = (payload) => {
  toastStore.dispatch("addMessage", {
      message: payload.data.message, 
      icon: payload.data.icon || 'info', 
      ttl: payload.data.ttl || 3000
  });
  appEvents.emit('schedule:refresh')
}

let previousUserId = null;
userStore.watch( (state) => state.userInfo?.id, (newId) => {
  if (newId && newId !== previousUserId) {
      previousUserId = newId;
      const currentUser = userStore.state.userInfo;
      restoreChannels()
      subscribeToChannel("notification")
      subscribeToChannel('notification::user::' + newId, 'notification::user::' + newId)
      
      // Silently sync push subscription to ensure backend has the latest token
      pushService.sync();

      // Initialize RevenueCat for native in-app purchases with authenticated user ID
      if (isNativePlatform()) {
          initRevenueCat(newId);
      }

      // Silently sync browser timezone if not set or default
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && currentUser && (!currentUser.timezone || currentUser.timezone === 'UTC' || currentUser.timezone === '')) {
          fetch('/api/user/profile', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: currentUser.name, timezone: browserTz })
          }).then(() => {
              userStore.dispatch('updateBasicInfo');
          }).catch(err => console.error('Failed to sync timezone:', err));
      }
  }
}, { immediate: true })

</script>